/**
 * Constructs a "Style" configuration tab for a widget editor, allowing users
 * to define layout-related styles such as width, alignment, and padding.
 *
 * @param {WidgetProps<any, any>} props - The widget's properties used to build context for the editor tab.
 * @param {object} [params] - Optional parameters to customize the tab content.
 * @param {boolean} [params.align] - Whether to include alignment options (left, center, right).
 * @param {DefaultFormWidgetConfigTypes[]} [params.children] - Additional form widgets to include in the style tab.
 * @param {(data: any) => any} [params.onLoad] - Optional hook to transform data when the tab loads.
 * @param {(data: any) => any} [params.onSubmit] - Optional hook to transform or clean up data before submission.
 * @returns {EditorTab} The generated EditorTab object representing the style configuration UI.
 */
export function buildStyleTab(props, params) {
    const children = [];
    if (params === null || params === void 0 ? void 0 : params.align) {
        children.push({
            type: 'form.radio',
            key: 'style.align',
            label: 'Align',
            defaultValue: 'left',
            values: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
            style: {
                inline: true,
            },
        });
    }
    if (params === null || params === void 0 ? void 0 : params.children) {
        children.push(...params.children);
    }
    const allChildren = [
        {
            type: 'form.panel',
            children: [
                {
                    type: 'form.panel',
                    children: [
                        {
                            type: 'form.number',
                            key: 'style.width',
                            label: 'Width',
                            validate: {
                                min: 0,
                            },
                            style: {
                                width: 200,
                            },
                        },
                        ...children,
                    ],
                },
                {
                    type: 'form.panel',
                    children: [
                        {
                            type: 'form.number',
                            key: 'style.paddingTop',
                            label: 'Padding Top',
                            validate: {
                                min: 0,
                            },
                            style: {
                                width: 164,
                            },
                        },
                        {
                            type: 'form.panel',
                            children: [
                                {
                                    type: 'form.number',
                                    key: 'style.paddingLeft',
                                    label: 'Padding Left',
                                    validate: {
                                        min: 0,
                                    },
                                    style: {
                                        width: 148,
                                    },
                                },
                                {
                                    type: 'form.number',
                                    key: 'style.paddingRight',
                                    label: 'Padding Right',
                                    validate: {
                                        min: 0,
                                    },
                                    style: {
                                        width: 148,
                                    },
                                },
                            ],
                            style: {
                                layout: 'row',
                                horizontalAlign: 'center',
                            },
                        },
                        {
                            type: 'form.number',
                            key: 'style.paddingBottom',
                            label: 'Padding Bottom',
                            validate: {
                                min: 0,
                            },
                            style: {
                                width: 164,
                            },
                        },
                    ],
                    style: {
                        horizontalAlign: 'center',
                    },
                },
            ],
            style: {
                layout: 'row',
            },
        },
    ];
    const onLoad = (data) => {
        if (params === null || params === void 0 ? void 0 : params.onLoad) {
            data = params.onLoad(data);
        }
        return data;
    };
    const onSubmit = (data) => {
        var _a, _b;
        if (params === null || params === void 0 ? void 0 : params.align) {
            if (((_a = data.style) === null || _a === void 0 ? void 0 : _a.align) === 'left') {
                (_b = data.style) === null || _b === void 0 ? true : delete _b.align;
                if (Object.keys(data.style).length === 0) {
                    delete data.style;
                }
            }
        }
        if (params === null || params === void 0 ? void 0 : params.onSubmit) {
            data = params.onSubmit(data);
        }
        return data;
    };
    return { label: 'Style', children: allChildren, onLoad, onSubmit };
}
